import React, {useCallback, useMemo, useState} from 'react';
import {Image, message, Tag, Timeline} from 'antd';
import {formatTimestampToDateTime} from "../utils/dateTimeUtils";
import {useQuery} from "@apollo/client";
import { useTranslation } from 'react-i18next';
import { t } from '../i18n';
import {
    ExceptionDictionaryResponse,
    GET_ALL_EXCEPTION
} from "../pages/delivery/parcels/graphql/queries/getAllException";
import {
    GET_DELIVERY_METHOD_I18N_CONFIG,
    GetDeliveryMethodI18nConfigResponse
} from "../pages/config/common-config/graphql/getDeliveryMethodI18nConfig";
import {
    GetPickupExceptionReasonsResponse,
    GET_PICKUP_EXCEPTION_REASONS
} from "../pages/pickup/appointment/graphql/queries/getPickupExceptionReasons";
import { useStatusInternationalization } from '../pages/delivery/parcels/hooks/useStatusInternationalization';
import {formatWeightKgToLb, formatLengthCmToInch} from "../utils/numberUtils";
import { getOrderNoteText } from '../utils/orderNoteUtils';
import { SorterType, SorterTypeTranslationKey } from '../enums/SorterEnum';
import type { CameraPhotoInfo, ExternalOrderEventSummaryType, PackageInfoChangeType } from '../graphql/types/OrderDetailType';
import {
    formatCompareEventTime,
    getDisplayDescription,
    getDisplayLocation,
} from './ExternalParcelsTimeline';
import { TmsDepartureEventTypeEnum } from '../enums/TmsEnum';
import {
    parseBaggingScanNoMatchNotes,
    splitCandidateBagsForDisplay,
} from '../utils/logScanNotesDisplayUtils';
import { buildPackageInfoComparison, buildSubmittedValues } from '../utils/packageInfoChangeDisplay';

/**
 * 外部业务类型 int → name 展示映射（对齐后端 [ExternalBusinessType.description]）。
 * PC 装包轨迹展示直接用 enum name，不走 i18n；未知值走 fallback 不渲染。
 * 后端约定：仅 PPMOD 时下发本字段，NORMAL 及非 PP 场景一律 null → 前端 skip。
 */
const EXTERNAL_BUSINESS_TYPE_NAME: Record<number, string> = {
    0: 'NORMAL',
    1: 'PPMOD',
};

/**
 * 轨迹对比：按内部 eventId 挂载对外轨迹；unmapped 在时间线末尾单独成组。
 * 挂载的外部轨迹时间使用 `formatCompareEventTime`（浏览器本地时区），便于与内部事件时间对照。
 */
export interface ExternalComparePayload {
    byEventId: Map<string, ExternalOrderEventSummaryType[]>;
    unmapped: ExternalOrderEventSummaryType[];
}

interface CustomParcelsTimelineProps {
    items: Array<{
        eventId?: string;
        type?: string;
        title?: string;
        time?: string;
        details?: string[];
        imageS3UrlsJson?: string[],
        cameraPhotos?: CameraPhotoInfo[] | null,
        nextAction?: string,
        exceptionReasonCode?: string,
        exceptionSubReasons?: string[],
        channelId?: string,
        channelApiVersion?: number
        phoneNumber?: string,
        failReason?: string,
        operationSource?: string,
        appointmentNumber?: string,
        driverAccountId?: string,
        driverName?: string,
        driverPhone?: string,
        dspId?: string,
        dspName?: string,
        operatorName?: string,
        operatorPhone?: string,
        operatorRole?: string,
        operatorWarehouseName?: string,
        operatorDspName?: string,
        pickupHubName?: string,
        sortingMachineCode?: string,
        sortingMachineType?: SorterType,
        currentWarehouseName?: string,
        bagNumber?: string,
        clientCartonNo?: string,
        presortCode?: string,
        originalScanAction?: string,
        bagSortingCode?: string,
        scanSource?: string,
        loadRemoveType?: string,
        bagTaskName?: string,
        fromBagNumber?: string,
        toBagNumber?: string,
        deliveryAttempts?: number,
        licensePlate?: string,
        sealNo?: string,
        deliveryDistance?: number,
        deliveryMethod?: string,
        latitude?: number,
        longitude?: number,
        weightKg?: number,
        originalEventType?: string,  // LogScan事件的原始事件类型
        isDestHubArrived?: boolean,
        scStages?: string[],
        /** 外部业务类型（对齐后端 ExternalBusinessType.value）：0-NORMAL / 1-PPMOD；null=不展示 */
        externalBusinessType?: number | null,
        /** PPMOD 装包容器主键（快照） */
        bagContainerId?: number | null,
        /** PPMOD 装包容器展示名快照；null=不展示 */
        bagContainerName?: string | null,
        isFirstLmArrival?: boolean,
        operatePlatformType?: string,  // 操作平台类型（用于MarkAsException事件）
        messageKey?: string,  // 消息键（用于LogScan事件）
        notes?: string | null,  // 装包失败 LogScan 诊断详情
        departureEventType?: number,  // 发车事件类型：1-干线始发站发车 2-干线途经站发车 3-支线目的地HUB发车 4-交接
        carrierName?: string,  // 承运商名称
        revokeReason?: string,  // 撤回原因（用于DeliveryRevoked事件）
        // 地址变更事件专用字段
        oldRecipientAddress?: {
            id?: string;
            fullName?: string;
            addressLine1?: string;
            addressLine2?: string;
            city?: string;
            state?: string;
            zipcode?: string;
            phoneNumber?: string;
            email?: string;
            countryCode?: string;
            extension?: string;
            company?: string;
            instructions?: string;
        };
        newRecipientAddress?: {
            id?: string;
            fullName?: string;
            addressLine1?: string;
            addressLine2?: string;
            city?: string;
            state?: string;
            zipcode?: string;
            phoneNumber?: string;
            email?: string;
            countryCode?: string;
            extension?: string;
            company?: string;
            instructions?: string;
        };
        // 收件人联系人变更事件专用字段
        oldFullName?: string;
        oldPhoneCountryCode?: number;
        oldPhoneNumber?: string;
        oldPhoneExtension?: string | null;
        newFullName?: string;
        newPhoneCountryCode?: number;
        newPhoneNumber?: string;
        newPhoneExtension?: string | null;
        reason?: string,
        oldSortingRule?: string,
        newSortingRule?: string,
        podComment?: string[],
        // POD审核修改事件专用字段
        previousStatus?: string,
        newStatus?: string,
        previousReviewNotes?: string[],
        // 备注变更事件专用字段
        oldOrderNote?: {
            note?: string;
            lang?: string;
            orderNoteTranslations?: [{
                translateNote?: string;
                lang?: string;
            }]
        };
        newOrderNote?: {
            note?: string;
            lang?: string;
            orderNoteTranslations?: [{
                translateNote?: string;
                lang?: string;
            }]
        };
        // 司机投递备注（Delivered/PreDelivered/DeliveryFailed 事件）
        driverDeliveryNote?: {
            note?: string;
            lang?: string;
            orderNoteTranslations?: [{
                translateNote?: string;
                lang?: string;
            }]
        };
        // 实际尺寸变更事件专用字段
        oldLengthCm?: number;
        oldWidthCm?: number;
        oldHeightCm?: number;
        newLengthCm?: number;
        newWidthCm?: number;
        newHeightCm?: number;
        // 坐标变更事件专用字段
        oldLatitude?: number;
        oldLongitude?: number;
        newLatitude?: number;
        newLongitude?: number;
        addressLocationReviewStatus?: string;
        // 客户修改包裹信息事件专用字段（降级为 LogScan 时同样下发）
        packageInfoChange?: PackageInfoChangeType | null;
    }>;
    maxItemsBeforeCollapse?: number;
    channelName?: string;
    /** 开启轨迹对比时传入，由 ParcelHistorySection 根据 externalOrderEvents 构建 */
    externalCompare?: ExternalComparePayload;
}

type AddressLocationReviewStatusKey = 'PENDING' | 'APPROVED' | 'REJECTED' | 'OBSOLETE';

const ADDRESS_LOCATION_REVIEW_STATUS_STYLE: Record<
    AddressLocationReviewStatusKey,
    { tagColor: 'warning' | 'success' | 'error' | 'default'; borderClass: string }
> = {
    PENDING: { tagColor: 'warning', borderClass: 'border-amber-500' },
    APPROVED: { tagColor: 'success', borderClass: 'border-green-500' },
    REJECTED: { tagColor: 'error', borderClass: 'border-red-500' },
    OBSOLETE: { tagColor: 'default', borderClass: 'border-gray-400' },
};

/** 包裹信息对比卡片用：0 cm 是合法值，不像 formatLengthCmToInch 那样按缺值显示为空串 */
const formatPackageLengthCmToInch = (lengthCm: number): string => (lengthCm === 0 ? '0.00' : formatLengthCmToInch(lengthCm));

const CustomParcelsTimeline: React.FC<CustomParcelsTimelineProps> = ({
                                                           items,
                                                           maxItemsBeforeCollapse = 6,
                                                           channelName,
                                                           externalCompare
                                                       }) => {
    const [viewMore, setViewMore] = useState(false);
    const { data } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const { data: pickupExceptionData } = useQuery<GetPickupExceptionReasonsResponse>(GET_PICKUP_EXCEPTION_REASONS);
    const { data: deliveryMethodI18nData } = useQuery<GetDeliveryMethodI18nConfigResponse>(GET_DELIVERY_METHOD_I18N_CONFIG, { fetchPolicy: 'cache-first' });
    const deliveryMethodI18nConfig = deliveryMethodI18nData?.getDeliveryMethodI18nConfig ?? [];
    
    // 使用 useTranslation hook 获取当前语言，这样当语言切换时会自动触发重新渲染
    const { i18n } = useTranslation();
    const currentLang = i18n.resolvedLanguage || i18n.language || 'en';
    
    // 使用状态国际化hook
    const { getNextActionTextFromBackend } = useStatusInternationalization();

    // 工具函数：将事件类型转换为国际化显示名称
    const getLocalizedEventType = useCallback((eventType: string): string => {
        if (!eventType) return eventType;
        return t(`enum.orderEvent.${eventType}`) || eventType;
    }, []);

    // 格式化地址信息
    const formatAddress = useCallback((address: any): string => {
        if (!address) return '';
        const parts = [
            address.addressLine1,
            address.addressLine2,
            `${address.city}, ${address.state}, ${address.zipcode}, US`
        ].filter(Boolean);
        return parts.join('\n');
    }, []);

    // 格式化尺寸信息（显示为英寸）；toInch 可换成区分 0 与缺值的格式化函数
    const formatDimensions = useCallback((lengthCm: number | null | undefined, widthCm: number | null | undefined, heightCm: number | null | undefined, toInch: (lengthCm: number) => string = formatLengthCmToInch): string => {
        const parts = [
            lengthCm != null ? `${t('components.timeline.length')}: ${toInch(lengthCm)}"` : null,
            widthCm != null ? `${t('components.timeline.width')}: ${toInch(widthCm)}"` : null,
            heightCm != null ? `${t('components.timeline.height')}: ${toInch(heightCm)}"` : null,
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '-';
    }, [t]);

    // 包裹信息对比卡片用：体积重 0（任一边尺寸 ≤ 0 时的计算结果）是合法值，不像 formatWeightKgToLb 那样按缺值显示为“-”
    const formatPackageWeightKgToLb = useCallback((weightKg: number | null): string => (
        weightKg === 0 ? `0.00 ${t('delivery.parcels.label.lb')}` : formatWeightKgToLb(weightKg)
    ), [t]);

    const renderAddressLocationReviewStatusBadge = useCallback((status?: string) => {
        if (!status) {
            return null;
        }
        const normalized = status.toUpperCase() as AddressLocationReviewStatusKey;
        const style = ADDRESS_LOCATION_REVIEW_STATUS_STYLE[normalized];
        if (!style) {
            return (
                <Tag className="text-sm font-medium px-3 py-0.5 m-0">
                    {status}
                </Tag>
            );
        }
        return (
            <Tag
                color={style.tagColor}
                bordered={false}
                className="text-sm font-semibold px-3 py-0.5 m-0 leading-6"
            >
                {t(`components.timeline.addressLocationReviewStatusValues.${normalized}`, status)}
            </Tag>
        );
    }, [t]);

    // 格式化POD评论的国际化文本
    const formatPodComment = useCallback((podComment: string[] | undefined): string => {
        if (!podComment) return '';

        const formattedComments = podComment.map(comment => {
            const key = `podReview.reasons.${comment}`;
            const translated = t(key);
            // 如果 i18n 命中则使用翻译值，否则返回原值
            return translated !== key ? translated : comment;
        });

        return formattedComments.join(', ');
    }, [t]);

    // 获取异常原因文本（使用响应式的 currentLang）
    const getExceptionReasonText = useCallback((exceptionReasonCode: string, data?: ExceptionDictionaryResponse): string => {
        if (!data || !data.getExceptionDictionaryForHeadquarters) {
            return exceptionReasonCode;
        }
        
        const exception = data.getExceptionDictionaryForHeadquarters.find(
            item => item.exceptionReasonCode === exceptionReasonCode
        );
        
        if (!exception) {
            return exceptionReasonCode;
        }

        if (currentLang === 'zh' && exception.secondaryTypeZh) {
            return `${exception.secondaryTypeZh}`;
        } else if (currentLang === 'es' && exception.secondaryTypeEs) {
            return `${exception.secondaryTypeEs}`;
        } else if (exception.secondaryTypeEn) {
            return `${exception.secondaryTypeEn}`;
        }
        return `${exception.exceptionReasonCode}`;
    }, [currentLang]);

    // 根据一级原因码与二级原因 code 列表，从异常字典中解析出二级原因文案（当前语言）
    const getExceptionSubReasonTexts = useCallback((
        exceptionReasonCode: string,
        subReasonCodes: string[],
        data?: ExceptionDictionaryResponse
    ): string[] => {
        if (!data?.getExceptionDictionaryForHeadquarters || !subReasonCodes?.length) return [];
        const exception = data.getExceptionDictionaryForHeadquarters.find(
            item => item.exceptionReasonCode === exceptionReasonCode
        );
        if (!exception?.exceptionSubReasons?.length) return [];
        return subReasonCodes.map(code => {
            const sub = exception.exceptionSubReasons!.find(s => s.code === code);
            if (!sub) return code;
            if (currentLang === 'zh' && sub.textZh) return sub.textZh;
            if (currentLang === 'es' && sub.textEs) return sub.textEs;
            return sub.textEn || code;
        }).filter(Boolean);
    }, [currentLang]);

    // 获取揽收失败原因文本
    const getPickUpFailReasonText = useCallback((failReason: string): string => {
        if (!failReason) return '';
        
        if (pickupExceptionData?.getExceptionDictionaryForPickup) {
            const pickupException = pickupExceptionData.getExceptionDictionaryForPickup.find(
                item => item.exceptionReasonCode === failReason
            );
            
            if (pickupException) {
                switch (currentLang) {
                    case 'zh':
                        return pickupException.secondaryTypeZh;
                    case 'es':
                        return pickupException.secondaryTypeEs;
                    case 'en':
                    default:
                        return pickupException.secondaryTypeEn;
                }
            }
        }

        // 如果找不到对应的异常字典，直接返回原始文本
        return failReason;
    }, [pickupExceptionData, currentLang]);

    const displayedItems = useMemo(() => {
        if (!items || items.length === 0) return [];

        const internalNodes = items.slice().reverse().map((item, index) => ({
            key: index,
            color: index === 0 ? 'blue' : 'gray',
            children: (
                <div className={`${item.type === 'Delivered' ? 'font-medium' : ''} space-y-1`} key={index}>
                    <div className="flex justify-between">
                        <span className="text-base mr-6">
                            {item.type ? (() => {
                                const translationKey = `enum.orderEvent.${item.type.substring(14)}`;
                                const translatedText = t(translationKey);
                                return translatedText === translationKey ? (item.title || item.type.substring(14)) : translatedText;
                            })() : (item.title || '-')}
                        </span>
                        <span className="text-sm">{item.time ? formatTimestampToDateTime(item.time) : '-'}</span>
                    </div>
                    {/* 对于OrderEventDataDriverChanged事件，先显示新司机信息，再显示操作人信息 */}
                    {item.type === 'OrderEventDataDriverChanged' && (item.driverName || item.driverPhone || item.dspName) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.driverName && (
                                <div>{`${t('components.timeline.newDriverName')}: ${item.driverName}`}</div>
                            )}
                            {item.driverPhone && (
                                <div>{`${t('components.timeline.newDriverPhone')}: ${item.driverPhone}`}</div>
                            )}
                            {item.dspName && (
                                <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                            )}
                        </div>
                    )}
                    {/* 合包事件的特殊展示 */}
                    {item.type === 'OrderEventDataMergeBag' && (item.fromBagNumber || item.toBagNumber) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.fromBagNumber && (
                                <div>{`${t('components.timeline.fromBagNumber')}: ${item.fromBagNumber}`}</div>
                            )}
                            {item.toBagNumber && (
                                <div>{`${t('components.timeline.toBagNumber')}: ${item.toBagNumber}`}</div>
                            )}
                        </div>
                    )}
                    {/* 操作人信息展示 */}
                    {(item.operatorName || item.operatorPhone || item.operatorRole || item.operatorWarehouseName || item.operatorDspName) ? (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.operatorName && (
                                <div>{`${t('components.timeline.operatorName')}: ${item.operatorName}`}</div>
                            )}
                            {item.operatorPhone && (
                                <div>{`${t('components.timeline.operatorPhone')}: ${item.operatorPhone}`}</div>
                            )}
                            {item.operatorRole && (
                                <div>{`${t('components.timeline.operatorRole')}: ${item.operatorRole}`}</div>
                            )}
                            {item.operatorWarehouseName && (
                                <div>{`${t('components.timeline.operatorWarehouseName')}: ${item.operatorWarehouseName}`}</div>
                            )}
                            {item.operatorDspName && (
                                <div>{`${t('components.timeline.operatorDspName')}: ${item.operatorDspName}`}</div>
                            )}
                            {item.revokeReason && (
                                <div>{`${t('components.timeline.revokeReasonLabel')}: ${t(`components.timeline.revokeReason.${item.revokeReason}`, { defaultValue: item.revokeReason })}`}</div>
                            )}
                        </div>
                    ) : (
                        <div className="text-gray-500 text-sm">
                            {((): React.ReactNode => {
                                if (item?.channelId && item?.channelApiVersion && channelName) {
                                    return `${t('components.timeline.by')} ${channelName}-V${item?.channelApiVersion}`;
                                }

                                const detail = item.details?.[0] || '-';
                                if (detail.startsWith('by ')) {
                                    const content = detail.substring(3);
                                    return `${t('components.timeline.by')} ${content}`;
                                }
                                return detail;
                            })()}
                        </div>
                    )}
                    {item.operationSource && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.source')}: ${t(`components.timeline.operationSource.${item.operationSource}`, { defaultValue: item.operationSource })}`}
                        </div>
                    )}
                    {/* 显示评论（如机审失败原因） */}
                    {item.podComment && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.podComment')}${formatPodComment(item.podComment)}`}
                        </div>
                    )}
                    {item.failReason && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.reason')}: ${getPickUpFailReasonText(item.failReason)}`}
                        </div>
                    )}
                    {item.exceptionReasonCode && (
                        <div className="text-gray-500 text-sm">
                            {(() => {
                                const primaryText = getExceptionReasonText(item.exceptionReasonCode, data);
                                const subTexts = item.exceptionSubReasons?.length
                                    ? getExceptionSubReasonTexts(item.exceptionReasonCode, item.exceptionSubReasons, data)
                                    : [];
                                const reasonStr = subTexts.length > 0
                                    ? `${primaryText} - ${subTexts.join(', ')}`
                                    : primaryText;
                                return `${t('components.timeline.reason')}: ${reasonStr}`;
                            })()}
                        </div>
                    )}
                    {item.nextAction && <div className="text-gray-500 text-sm">{`${t('components.timeline.nextAction')}: ${getNextActionTextFromBackend(item.nextAction)}`}</div>}

                    {/* 预约揽收创建事件的特殊展示 - 显示预约单号 */}
                    {item.type === 'OrderEventDataPickupAppointmentCreated' && item.appointmentNumber && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.appointmentNumber')}: ${item.appointmentNumber}`}
                        </div>
                    )}

                    {/* 指派揽收司机事件的特殊展示 - 显示司机信息 */}
                    {item.type === 'OrderEventDataPickupTaskAssignment' && (item.driverName || item.driverPhone || item.dspName) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.driverName && (
                                <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                            )}
                            {item.driverPhone && (
                                <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                            )}
                            {item.dspName && (
                                <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                            )}
                        </div>
                    )}

                    {/* 入库扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataArrivalScan' &&
                     (item.pickupHubName || item.sortingMachineCode || (item.weightKg !== null && item.weightKg !== undefined) || item.isDestHubArrived || item.scStages?.length) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.pickupHubName && (
                                <div>{`${t('components.timeline.pickupHubName')}: ${item.pickupHubName}`}</div>
                            )}
                            {item.sortingMachineCode && (
                                <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                            )}
                            {item.sortingMachineType && (
                                <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                            )}
                            {item.weightKg !== null && item.weightKg !== undefined && (
                                <div>{`${t('components.timeline.weight')}: ${formatWeightKgToLb(item.weightKg)}`}</div>
                            )}
                            {item.isDestHubArrived && (
                                <div>{`${t('components.timeline.isDestHubArrived')}: ${t('common.yes')}`}</div>
                            )}
                            {item.scStages && item.scStages.length > 0 && (
                                <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                            )}
                        </div>
                    )}

                    {/* 揽收到达扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataPickupArrivalScan' &&
                     (item.pickupHubName || item.sortingMachineCode || (item.weightKg !== null && item.weightKg !== undefined) || item.isDestHubArrived || item.scStages?.length || item.isFirstLmArrival || item.bagNumber) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.pickupHubName && (
                                <div>{`${t('components.timeline.pickupHubName')}: ${item.pickupHubName}`}</div>
                            )}
                            {item.sortingMachineCode && (
                                <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                            )}
                            {item.sortingMachineType && (
                                <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                            )}
                            {item.weightKg !== null && item.weightKg !== undefined && (
                                <div>{`${t('components.timeline.weight')}: ${formatWeightKgToLb(item.weightKg)}`}</div>
                            )}
                            {item.isDestHubArrived && (
                                <div>{`${t('components.timeline.isDestHubArrived')}: ${t('common.yes')}`}</div>
                            )}
                            {item.scStages && item.scStages.length > 0 && (
                                <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                            )}
                            {item.isFirstLmArrival && (
                                <div>{`${t('components.timeline.isFirstLmArrival')}: ${t('common.yes')}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                        </div>
                    )}

                    {/* 中转到达扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataTransferArrivalScan' &&
                     (item.currentWarehouseName || item.bagNumber || item.isDestHubArrived || item.scStages?.length || item.isFirstLmArrival || item.sortingMachineCode) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.isDestHubArrived && (
                                <div>{`${t('components.timeline.isDestHubArrived')}: ${t('common.yes')}`}</div>
                            )}
                            {item.scStages && item.scStages.length > 0 && (
                                <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                            )}
                            {item.isFirstLmArrival && (
                                <div>{`${t('components.timeline.isFirstLmArrival')}: ${t('common.yes')}`}</div>
                            )}
                            {item.sortingMachineCode && (
                                <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                            )}
                            {item.sortingMachineType && (
                                <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                            )}
                        </div>
                    )}

                    {/* 大包扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataClientCartonScan' && item.currentWarehouseName && (
                        <div className="text-gray-500 text-sm space-y-1">
                            <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                        </div>
                    )}

                    {/* 客户大包号:任意事件类型,只要有值就展示 */}
                    {item.clientCartonNo && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.clientCartonNo')}: ${item.clientCartonNo}`}
                        </div>
                    )}

                    {/* 大包预分拣码:有值就展示（大包扫描事件携带） */}
                    {item.presortCode && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.presortCode')}: ${item.presortCode}`}
                        </div>
                    )}

                    {/* 原始扫描动作:信任交接回补入库的到件事件携带 */}
                    {item.originalScanAction && (
                        <div className="text-gray-500 text-sm">
                            {`${t('components.timeline.originalScanAction')}: ${t(`components.timeline.originalScanActionNames.${item.originalScanAction}`, item.originalScanAction)}`}
                        </div>
                    )}

                    {/* 退货扫描事件的特殊展示 */}
                    {(item.type === 'OrderEventDataReturnScan' || item.type === 'OrderEventDataReturnArrivalScan') &&
                     item.currentWarehouseName && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                        </div>
                    )}

                    {/* 订单已退件事件的特殊展示 - 移除重复的操作人信息显示 */}

                    {/* 包裹移出包牌事件（大包预告对账移除）的特殊展示 */}
                    {item.type === 'OrderEventDataBagParcelRemoved' && (item.bagNumber || item.bagSortingCode || item.scanSource || item.reason) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.bagSortingCode && (
                                <div>{`${t('components.timeline.bagSortingCode')}: ${item.bagSortingCode}`}</div>
                            )}
                            {item.scanSource && (
                                <div>{`${t('components.timeline.scanSource')}: ${t(`components.timeline.scanSourceNames.${item.scanSource}`, item.scanSource)}`}</div>
                            )}
                            {item.reason && (
                                <div>{`${t('components.timeline.reason')}: ${item.reason}`}</div>
                            )}
                        </div>
                    )}

                    {/* 装包扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataBaggingScan' && (item.currentWarehouseName || item.bagNumber || item.bagSortingCode || item.scanSource || item.sortingMachineCode || item.scStages?.length || item.externalBusinessType != null || item.bagContainerName) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.bagSortingCode && (
                                <div>{`${t('components.timeline.bagSortingCode')}: ${item.bagSortingCode}`}</div>
                            )}
                            {item.scanSource && (
                                <div>{`${t('components.timeline.scanSource')}: ${t(`components.timeline.scanSourceNames.${item.scanSource}`, item.scanSource)}`}</div>
                            )}
                            {item.sortingMachineCode && (
                                <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                            )}
                            {item.sortingMachineType && (
                                <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                            )}
                            {item.scStages && item.scStages.length > 0 && (
                                <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                            )}
                            {item.externalBusinessType != null && EXTERNAL_BUSINESS_TYPE_NAME[item.externalBusinessType] && (
                                <div>{`${t('components.timeline.externalBusinessType')}: ${EXTERNAL_BUSINESS_TYPE_NAME[item.externalBusinessType]}`}</div>
                            )}
                            {item.bagContainerName && (
                                <div>{`${t('components.timeline.bagContainerType')}: ${item.bagContainerName}`}</div>
                            )}
                        </div>
                    )}

                    {/* 分拣扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataSortingScan' && (item.currentWarehouseName || item.bagNumber || item.scStages?.length) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.scStages && item.scStages.length > 0 && (
                                <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                            )}
                        </div>
                    )}

                    {/* 拆包扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataUnbaggingScan' && (item.currentWarehouseName || item.bagNumber || item.bagSortingCode) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.bagSortingCode && (
                                <div>{`${t('components.timeline.bagSortingCode')}: ${item.bagSortingCode}`}</div>
                            )}
                        </div>
                    )}

                    {/* 发件扫描事件的特殊展示 */}
                    {(item.type === 'OrderEventDataDepartureScan' || item.type === 'OrderEventDataTransportDepartureScan') && (item.currentWarehouseName || item.bagNumber || item.scStages?.length) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.scStages && item.scStages.length > 0 && (
                                <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                            )}
                        </div>
                    )}

                    {/* 交接事件的特殊展示 */}
                    {item.type === 'OrderEventDataHandedOver' && item.scStages && item.scStages.length > 0 && (
                        <div className="text-gray-500 text-sm space-y-1">
                            <div>{`${t('components.timeline.scStages')}: ${item.scStages.map(s => t(`components.timeline.scStageNames.${s}`, s)).join(', ')}`}</div>
                        </div>
                    )}

                    {/* 装车扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataLoadingScan' && (item.currentWarehouseName || item.bagNumber || item.licensePlate) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.licensePlate && (
                                <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                            )}
                        </div>
                    )}

                    {/* 装车移出扫描：站点、包牌、车牌、移出类型 */}
                    {item.type === 'OrderEventDataLoadRemovalScan' && (item.currentWarehouseName || item.bagNumber || item.licensePlate || item.loadRemoveType) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.licensePlate && (
                                <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                            )}
                            {item.loadRemoveType && (
                                <div>{`${t('components.timeline.loadRemoveType')}: ${t(`components.timeline.loadRemoveTypeNames.${item.loadRemoveType}`, item.loadRemoveType)}`}</div>
                            )}
                        </div>
                    )}

                    {/* 包牌改流向事件专用展示：站点 + 包牌码 + 原分拣码 + 新分拣码 */}
                    {item.type === 'OrderEventDataBagReship' && (item.currentWarehouseName || item.bagNumber || item.oldSortingRule || item.newSortingRule) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.oldSortingRule && (
                                <div>{`${t('components.timeline.oldSortingRule')}: ${item.oldSortingRule}`}</div>
                            )}
                            {item.newSortingRule && (
                                <div>{`${t('components.timeline.newSortingRule')}: ${item.newSortingRule}`}</div>
                            )}
                        </div>
                    )}

                    {/* 发车事件的特殊展示 */}
                    {item.type === 'OrderEventDataHubDeparted' && (item.currentWarehouseName || item.carrierName || item.driverName || item.driverPhone || item.licensePlate || item.departureEventType) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.carrierName && (
                                <div>{`${t('components.timeline.carrierName')}: ${item.carrierName}`}</div>
                            )}
                            {item.driverName && (
                                <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                            )}
                            {item.driverPhone && (
                                <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                            )}
                            {item.licensePlate && (
                                <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                            )}
                            {item.departureEventType != null && TmsDepartureEventTypeEnum[item.departureEventType] && (
                                <div>{`${t('components.timeline.departureEventType')}: ${t(`components.timeline.departureEventTypeValue.${TmsDepartureEventTypeEnum[item.departureEventType]}`)}`}</div>
                            )}
                        </div>
                    )}

                    {/* 到达事件的特殊展示 */}
                    {item.type === 'OrderEventDataHubArrived' && (item.currentWarehouseName || item.carrierName || item.driverName || item.driverPhone || item.licensePlate) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.carrierName && (
                                <div>{`${t('components.timeline.carrierName')}: ${item.carrierName}`}</div>
                            )}
                            {item.driverName && (
                                <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                            )}
                            {item.driverPhone && (
                                <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                            )}
                            {item.licensePlate && (
                                <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                            )}
                        </div>
                    )}

                    {/* 封发车扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataSealingScan' && (item.currentWarehouseName || item.licensePlate || item.sealNo) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.licensePlate && (
                                <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                            )}
                            {item.sealNo && (
                                <div>{`${t('components.timeline.sealNo')}: ${item.sealNo}`}</div>
                            )}
                        </div>
                    )}

                    {/* 解封车扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataUnsealingScan' && (item.currentWarehouseName || item.licensePlate || item.sealNo) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.licensePlate && (
                                <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                            )}
                            {item.sealNo && (
                                <div>{`${t('components.timeline.sealNo')}: ${item.sealNo}`}</div>
                            )}
                        </div>
                    )}

                    {/* WHS到件扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataWhsArrivalScan' && (item.currentWarehouseName || item.bagNumber || item.originalEventType) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.originalEventType && (
                                <div>{`${t('components.timeline.originalEventType')}: ${getLocalizedEventType(item.originalEventType)}`}</div>
                            )}
                        </div>
                    )}

                    {/* 包任务相关事件的特殊展示 */}
                    {(item.type === 'OrderEventDataGenerateBagTask' ||
                      item.type === 'OrderEventDataDissolveBagTask' ||
                      item.type === 'OrderEventDataMoveOutFromBagTask' ||
                      item.type === 'OrderEventDataMoveInToBagTask') &&
                     (item.bagTaskName || item.bagNumber || item.bagSortingCode) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.bagTaskName && (
                                <div>{`${t('components.timeline.bagTaskName')}: ${item.bagTaskName}`}</div>
                            )}
                            {item.bagNumber && (
                                <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                            )}
                            {item.bagSortingCode && (
                                <div>{`${t('components.timeline.bagSortingCode')}: ${item.bagSortingCode}`}</div>
                            )}
                        </div>
                    )}

        {/* 分配给司机事件的特殊展示 */}
        {item.type === 'OrderEventDataAssignedToDriver' && (item.driverName || item.driverPhone || item.dspName) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
            </div>
        )}


        {/* 卸车扫描事件的特殊展示 */}
        {item.type === 'OrderEventDataUnloadingScan' && (item.currentWarehouseName || item.bagNumber || item.licensePlate) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.currentWarehouseName && (
                    <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                )}
                {item.bagNumber && (
                    <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                )}
                {item.licensePlate && (
                    <div>{`${t('components.timeline.licensePlate')}: ${item.licensePlate}`}</div>
                )}
            </div>
        )}

        {/* 日志扫描事件的特殊展示 */}
            {item.type === 'OrderEventDataLogScan' && (item.currentWarehouseName || item.dspName || item.originalEventType || item.messageKey || item.bagNumber || item.sortingMachineCode || item.notes || item.oldSortingRule || item.newSortingRule) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.currentWarehouseName && (
                    <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
                {item.originalEventType && (
                    <div>{`${t('components.timeline.originalEventType')}: ${getLocalizedEventType(item.originalEventType)}`}</div>
                )}
                {item.bagNumber && (
                    <div>{`${t('components.timeline.bagNumber')}: ${item.bagNumber}`}</div>
                )}
                {/* 改流向失败的 LogScan 把原事件挂在 originalEvent 上，后端据此透出原/新分拣规则，展示与成功的 BagReship 节点一致 */}
                {item.oldSortingRule && (
                    <div>{`${t('components.timeline.oldSortingRule')}: ${item.oldSortingRule}`}</div>
                )}
                {item.newSortingRule && (
                    <div>{`${t('components.timeline.newSortingRule')}: ${item.newSortingRule}`}</div>
                )}
                {item.messageKey && (
                    <div>{`${t('components.timeline.reason')}: ${t(item.messageKey, { destination: item.notes ?? '', defaultValue: item.messageKey })}`}</div>
                )}
                {item.notes && item.messageKey !== 'errors.business.pda.unloadingScan.log.notLocalStation' && item.messageKey !== 'errors.business.pda.bindingErrorAtWHSArrivalScan' && (() => {
                    const parsed = parseBaggingScanNoMatchNotes(item.notes);
                    if (parsed) {
                        const bagLines = splitCandidateBagsForDisplay(parsed.candidateBags);
                        return (
                            <div className="break-all whitespace-pre-wrap space-y-1">
                                <div>{`${t('components.timeline.logScanDetails')}:`}</div>
                                <div className="pl-2 space-y-1">
                                    <div>{`${t('components.timeline.logScanTrackingNumber')}: ${parsed.trackingNumber}`}</div>
                                    <div>{`${t('components.timeline.logScanCandidateBags')}:`}</div>
                                    {bagLines.map((line, idx) => (
                                        <div key={`bag-${idx}`} className="pl-2">{line}</div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className="break-all whitespace-pre-wrap">
                            {`${t('components.timeline.logScanDetails')}: ${item.notes}`}
                        </div>
                    );
                })()}
                {item.sortingMachineCode && (
                    <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                )}
                {item.sortingMachineType && (
                    <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                )}
                {/* 装包失败 LogScan：originalEvent 为 OrderEventDataBaggingScan 时透出 PP + 容器快照 */}
                {item.externalBusinessType != null && EXTERNAL_BUSINESS_TYPE_NAME[item.externalBusinessType] && (
                    <div>{`${t('components.timeline.externalBusinessType')}: ${EXTERNAL_BUSINESS_TYPE_NAME[item.externalBusinessType]}`}</div>
                )}
                {item.bagContainerName && (
                    <div>{`${t('components.timeline.bagContainerType')}: ${item.bagContainerName}`}</div>
                )}
            </div>
        )}

        {/* 客户修改包裹信息降级为 LogScan 时：灰框只列本次提交的目标值（未生效），不显示体积重与修改前的值 */}
        {item.type === 'OrderEventDataLogScan' && item.originalEventType === 'CustomerPackageInfoChanged' && item.packageInfoChange && (() => {
            const submitted = buildSubmittedValues(item.packageInfoChange);
            if (!submitted.weight && !submitted.dimensions) {
                return null;
            }
            return (
            <div className="mt-2 p-2 bg-gray-100 rounded border text-sm text-gray-600 space-y-1">
                <div className="font-medium text-gray-700">{`${t('components.timeline.submittedNotApplied')}:`}</div>
                {submitted.weight && (
                    <div>{`${t('components.timeline.weight')}: ${formatPackageWeightKgToLb(submitted.weight.weightKg)}`}</div>
                )}
                {submitted.dimensions && (
                    <div>{`${t('components.timeline.packageDimensions')}: ${formatDimensions(submitted.dimensions.lengthCm, submitted.dimensions.widthCm, submitted.dimensions.heightCm, formatPackageLengthCmToInch)}`}</div>
                )}
            </div>
            );
        })()}

        {/* 派送扫描事件的特殊展示 */}
        {item.type === 'OrderEventDataDeliveryScan' && (item.driverName || item.driverPhone || item.dspName) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
            </div>
        )}

        {/* 出库派送事件的特殊展示 */}
        {item.type === 'OrderEventDataOutForDelivery' && (item.driverName || item.driverPhone || item.dspName) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
            </div>
        )}

        {/* 派件签收事件的特殊展示 */}
        {item.type === 'OrderEventDataDelivered' && (item.driverName || item.driverPhone || item.dspName || item.deliveryDistance || item.latitude || item.longitude || item.deliveryMethod) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
                {item.latitude !== undefined && item.latitude !== null && item.longitude !== undefined && item.longitude !== null  && (
                    <div>{`${t('components.timeline.deliveryLocation')}: ${item.latitude === 0 && item.longitude === 0 ? '0,0' : `${item.latitude.toFixed(7)}, ${item.longitude.toFixed(7)}`}`}</div>
                )}
                {item.deliveryDistance !== undefined && item.deliveryDistance !== null &&
                 item.latitude !== undefined && item.latitude !== null && item.longitude !== undefined && item.longitude !== null &&
                 (item.latitude !== 0 || item.longitude !== 0) && (
                    <div>{`${t('components.timeline.deliveryDistance',{distance: item.deliveryDistance.toFixed(2) })} `}</div>
                )}
                {item.deliveryMethod && deliveryMethodI18nConfig.length > 0 && (() => {
                    const opt = deliveryMethodI18nConfig.find((c) => c.code === item.deliveryMethod);
                    const langKey = (i18n.language || '').toLowerCase().includes('zh') ? 'zh' : (i18n.language || '').toLowerCase().includes('es') ? 'es' : 'en';
                    const label = opt ? (opt[langKey as keyof typeof opt] || opt.en) || opt.code : item.deliveryMethod;
                    return <div>{`${t('components.timeline.deliveryMethod')}: ${label}`}</div>;
                })()}
                {item.driverDeliveryNote && getOrderNoteText(item.driverDeliveryNote) && (
                    <div>{`${t('components.timeline.driverDeliveryNote')}: ${getOrderNoteText(item.driverDeliveryNote)}`}</div>
                )}
            </div>
        )}

        {/* 预妥投事件的特殊展示 */}
        {item.type === 'OrderEventDataPreDelivered' && (item.driverName || item.driverPhone || item.dspName || item.deliveryDistance || item.latitude || item.longitude || item.deliveryMethod) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
                {item.latitude !== undefined && item.latitude !== null && item.longitude !== undefined && item.longitude !== null  && (
                    <div>{`${t('components.timeline.deliveryLocation')}: ${item.latitude === 0 && item.longitude === 0 ? '0,0' : `${item.latitude.toFixed(7)}, ${item.longitude.toFixed(7)}`}`}</div>
                )}
                {item.deliveryDistance !== undefined && item.deliveryDistance !== null &&
                 item.latitude !== undefined && item.latitude !== null && item.longitude !== undefined && item.longitude !== null &&
                 (item.latitude !== 0 || item.longitude !== 0) && (
                    <div>{`${t('components.timeline.deliveryDistance',{distance: item.deliveryDistance.toFixed(2) })} `}</div>
                )}
                {item.deliveryMethod && deliveryMethodI18nConfig.length > 0 && (() => {
                    const opt = deliveryMethodI18nConfig.find((c) => c.code === item.deliveryMethod);
                    const langKey = (i18n.language || '').toLowerCase().includes('zh') ? 'zh' : (i18n.language || '').toLowerCase().includes('es') ? 'es' : 'en';
                    const label = opt ? (opt[langKey as keyof typeof opt] || opt.en) || opt.code : item.deliveryMethod;
                    return <div>{`${t('components.timeline.deliveryMethod')}: ${label}`}</div>;
                })()}
                {item.driverDeliveryNote && getOrderNoteText(item.driverDeliveryNote) && (
                    <div>{`${t('components.timeline.driverDeliveryNote')}: ${getOrderNoteText(item.driverDeliveryNote)}`}</div>
                )}
            </div>
        )}

        {/* 派送失败事件的特殊展示 */}
        {item.type === 'OrderEventDataDeliveryFailed' && (item.driverName || item.driverPhone || item.dspName || item.deliveryAttempts || item.deliveryDistance || item.latitude || item.longitude) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
                {item.deliveryAttempts && (
                    <div>{`${t('components.timeline.deliveryAttempts')}: ${item.deliveryAttempts}`}</div>
                )}
                {item.latitude !== undefined && item.latitude !== null && item.longitude !== undefined && item.longitude !== null && (
                    <div>{`${t('components.timeline.deliveryLocation')}: ${item.latitude === 0 && item.longitude === 0 ? '0,0' : `${item.latitude.toFixed(7)},${item.longitude.toFixed(7)}`}`}</div>
                )}
                {item.deliveryDistance !== undefined && item.deliveryDistance !== null &&
                 item.latitude !== undefined && item.latitude !== null && item.longitude !== undefined && item.longitude !== null &&
                 (item.latitude !== 0 || item.longitude !== 0) && (
                    <div>{`${t('components.timeline.deliveryDistance',{distance: item.deliveryDistance.toFixed(2)})} `}</div>
                )}
                {item.driverDeliveryNote && getOrderNoteText(item.driverDeliveryNote) && (
                    <div>{`${t('components.timeline.driverDeliveryNote')}: ${getOrderNoteText(item.driverDeliveryNote)}`}</div>
                )}
            </div>
        )}

        {/* 返仓扫描事件的特殊展示 */}
        {item.type === 'OrderEventDataReturnToWhsScan' && item.dspName && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
            </div>
        )}

        {/* 留仓扫描事件的特殊展示 */}
        {item.type === 'OrderEventDataHoldingScan' && item.dspName && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.dspName && (
                    <div>{`${t('components.timeline.dspName')}: ${item.dspName}`}</div>
                )}
            </div>
        )}

        {/* 揽收失败事件的特殊展示 */}
        {item.type === 'OrderEventDataPickupFailed' && (item.driverName || item.driverPhone) && (
            <div className="text-gray-500 text-sm space-y-1">
                {item.driverName && (
                    <div>{`${t('components.timeline.driverName')}: ${item.driverName}`}</div>
                )}
                {item.driverPhone && (
                    <div>{`${t('components.timeline.driverPhone')}: ${item.driverPhone}`}</div>
                )}
            </div>
        )}

                    {/* 称重扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataWeightParcelScan' && ((item.weightKg !== null && item.weightKg !== undefined) || item.sortingMachineCode) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.weightKg !== null && item.weightKg !== undefined && (
                                <div>{`${t('components.timeline.weight')}: ${formatWeightKgToLb(item.weightKg)}`}</div>
                            )}
                            {item.sortingMachineCode && (
                                <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                            )}
                            {item.sortingMachineType && (
                                <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                            )}
                        </div>
                    )}

                    {/* 上架扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataPutawayScan' && (item.currentWarehouseName) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                        </div>
                    )}

                    {/* 下架扫描事件的特殊展示 */}
                    {item.type === 'OrderEventDataPickingScan' && (item.currentWarehouseName) && (
                        <div className="text-gray-500 text-sm space-y-1">
                            {item.currentWarehouseName && (
                                <div>{`${t('components.timeline.currentWarehouseName')}: ${item.currentWarehouseName}`}</div>
                            )}
                        </div>
                    )}
                    {/* 地址变更事件的特殊展示 */}
                    {item.type === 'OrderEventDataRecipientAddressChanged' && (item.oldRecipientAddress || item.newRecipientAddress) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            <div className="text-sm space-y-2">
                                {item.oldRecipientAddress && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldAddress')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {formatAddress(item.oldRecipientAddress)}
                                        </div>
                                    </div>
                                )}

                                {item.newRecipientAddress && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newAddress')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {formatAddress(item.newRecipientAddress)}
                                        </div>
                                    </div>
                                )}
                                {item.reason && (
                                    <div>
                                        <span className="font-medium text-gray-700">{t('components.timeline.reason')}:</span>
                                        <span className="text-gray-600 ml-1">{t(`components.timeline.reasonValues.${item.reason}`, item.reason)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 收件人联系人变更事件的特殊展示 */}
                    {item.type === 'OrderEventDataRecipientContactChanged' && (item.oldFullName != null || item.newFullName != null || item.oldPhoneNumber != null || item.newPhoneNumber != null) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            <div className="text-sm space-y-2">
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldContact')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {[
                                            item.oldFullName,
                                            item.oldPhoneNumber
                                                ? `+${item.oldPhoneCountryCode ?? 1} ${item.oldPhoneNumber}${item.oldPhoneExtension ? ` ext:${item.oldPhoneExtension}` : ''}`
                                                : null
                                        ].filter(Boolean).join(' / ') || '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newContact')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {[
                                            item.newFullName,
                                            item.newPhoneNumber
                                                ? `+${item.newPhoneCountryCode ?? 1} ${item.newPhoneNumber}${item.newPhoneExtension ? ` ext:${item.newPhoneExtension}` : ''}`
                                                : null
                                        ].filter(Boolean).join(' / ') || '-'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 收件坐标变更事件的特殊展示 */}
                    {(item.type === 'OrderEventDataAddressLocationChangeSubmitted'
                        || item.type === 'OrderEventDataAddressLocationChangeReviewed')
                        && (item.oldLatitude != null || item.oldLongitude != null
                            || item.newLatitude != null || item.newLongitude != null) && (() => {
                        const reviewStatus = item.addressLocationReviewStatus?.toUpperCase() as AddressLocationReviewStatusKey | undefined;
                        const statusStyle = reviewStatus ? ADDRESS_LOCATION_REVIEW_STATUS_STYLE[reviewStatus] : undefined;
                        const borderClass = statusStyle?.borderClass ?? 'border-blue-500';
                        return (
                        <div className={`mt-3 p-3 bg-gray-50 rounded-md border-l-4 ${borderClass}`}>
                            <div className="text-sm space-y-2">
                                {item.addressLocationReviewStatus && (
                                    <div className="flex items-center justify-between gap-3 pb-2 border-b border-gray-200">
                                        <span className="font-medium text-gray-700 shrink-0">
                                            {t('components.timeline.addressLocationReviewStatus')}
                                        </span>
                                        {renderAddressLocationReviewStatusBadge(item.addressLocationReviewStatus)}
                                    </div>
                                )}
                                {(item.oldLatitude != null || item.oldLongitude != null) && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldLocation')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {`${item.oldLatitude ?? '-'},${item.oldLongitude ?? '-'}`}
                                        </div>
                                    </div>
                                )}
                                {(item.newLatitude != null || item.newLongitude != null) && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newLocation')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {`${item.newLatitude ?? '-'},${item.newLongitude ?? '-'}`}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })()}

                    {/* 实际尺寸变更事件的特殊展示 */}
                    {item.type === 'OrderEventDataActualDimensionChanged' && 
                     (item.oldLengthCm != null || item.oldWidthCm != null || item.oldHeightCm != null ||
                      item.newLengthCm != null || item.newWidthCm != null || item.newHeightCm != null ||
                      item.sortingMachineCode || item.sortingMachineType) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            <div className="text-sm space-y-2">
                                {(item.oldLengthCm != null || item.oldWidthCm != null || item.oldHeightCm != null) && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldDimensions')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {formatDimensions(item.oldLengthCm, item.oldWidthCm, item.oldHeightCm)}
                                        </div>
                                    </div>
                                )}

                                {(item.newLengthCm != null || item.newWidthCm != null || item.newHeightCm != null) && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newDimensions')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {formatDimensions(item.newLengthCm, item.newWidthCm, item.newHeightCm)}
                                        </div>
                                    </div>
                                )}

                                {/* 设备信息展示 */}
                                {(item.sortingMachineCode || item.sortingMachineType) && (
                                    <div className="text-gray-600 space-y-1">
                                        {item.sortingMachineCode && (
                                            <div>{`${t('components.timeline.sortingMachineCode')}: ${item.sortingMachineCode}`}</div>
                                        )}
                                        {item.sortingMachineType && (
                                            <div>{`${t('components.timeline.sortingMachineType')}: ${t(SorterTypeTranslationKey[item.sortingMachineType])}`}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* 客户修改包裹信息事件的特殊展示：修改前/修改后对比，均按公制字段换算为 lb/in 显示 */}
                    {item.type === 'OrderEventDataCustomerPackageInfoChanged' && item.packageInfoChange && (() => {
                        const comparison = buildPackageInfoComparison(item.packageInfoChange);
                        // 修改后框：本次修改的项加粗，未修改的项灰显并标注（未修改）；体积重每次都会重算，始终加粗、不走已改/未改判定
                        const getAfterRowClass = (changed: boolean): string => (changed ? 'font-semibold text-gray-800' : 'text-gray-400');
                        const renderUnchangedSuffix = (changed: boolean): React.ReactNode => (changed ? null : (
                            <span className="ml-1">{t('components.timeline.unchanged')}</span>
                        ));
                        const getUnitSystemText = (unitSystem: string | null): string => (unitSystem === 'imperial'
                            ? t('components.timeline.unitSystemImperial')
                            : t('components.timeline.unitSystemMetric'));
                        return (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            <div className="text-sm space-y-2">
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.packageInfoBefore')}:</div>
                                    <div className="text-gray-600 bg-white p-2 rounded border space-y-1">
                                        <div>{`${t('components.timeline.weight')}: ${formatPackageWeightKgToLb(comparison.before.weightKg)}`}</div>
                                        <div>{`${t('components.timeline.packageDimensions')}: ${formatDimensions(comparison.before.lengthCm, comparison.before.widthCm, comparison.before.heightCm, formatPackageLengthCmToInch)}`}</div>
                                        <div>{`${t('components.timeline.volumetricWeight')}: ${formatPackageWeightKgToLb(comparison.before.volumetricWeightKg)}`}</div>
                                    </div>
                                </div>
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.packageInfoAfter')}:</div>
                                    <div className="bg-white p-2 rounded border space-y-1">
                                        <div className={getAfterRowClass(comparison.weightChanged)}>
                                            {`${t('components.timeline.weight')}: ${formatPackageWeightKgToLb(comparison.after.weightKg)}`}
                                            {renderUnchangedSuffix(comparison.weightChanged)}
                                        </div>
                                        <div className={getAfterRowClass(comparison.dimensionsChanged)}>
                                            {`${t('components.timeline.packageDimensions')}: ${formatDimensions(comparison.after.lengthCm, comparison.after.widthCm, comparison.after.heightCm, formatPackageLengthCmToInch)}`}
                                            {renderUnchangedSuffix(comparison.dimensionsChanged)}
                                        </div>
                                        <div className="font-semibold text-gray-800">
                                            {`${t('components.timeline.volumetricWeight')}: ${formatPackageWeightKgToLb(comparison.after.volumetricWeightKg)}`}
                                        </div>
                                    </div>
                                </div>
                                {comparison.unitSwitch.switched && (
                                    <div className="text-gray-600">
                                        {`${t('components.timeline.inputUnitSystem')}: ${getUnitSystemText(comparison.unitSwitch.from)} → ${getUnitSystemText(comparison.unitSwitch.to)}`}
                                    </div>
                                )}
                            </div>
                        </div>
                        );
                    })()}

                    {/* 备注变更事件的特殊展示 */}
                    {item.type === 'OrderEventDataChangeNotes' && (item.oldOrderNote || item.newOrderNote) && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-blue-500">
                            <div className="text-sm space-y-2">
                                {item.oldOrderNote && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.oldNote')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {getOrderNoteText(item.oldOrderNote)}
                                        </div>
                                    </div>
                                )}

                                {item.newOrderNote && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.newNote')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {getOrderNoteText(item.newOrderNote)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* POD审核修改事件的特殊展示 */}
                    {item.type === 'OrderEventDataPodReviewModified' && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-md border-l-4 border-purple-500">
                            <div className="text-sm space-y-2">
                                {/* 修改前状态 */}
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.previousStatus')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {item.previousStatus ? (() => {
                                            const statusMap = {
                                                'MANUAL_APPROVED': t('enum.podReviewStatus.MANUAL_APPROVED'),
                                                'MANUAL_DISAPPROVED': t('enum.podReviewStatus.MANUAL_DISAPPROVED'),
                                                'MANUAL_CORRECTED': t('enum.podReviewStatus.MANUAL_CORRECTED')
                                            };
                                            return statusMap[item.previousStatus as keyof typeof statusMap] || item.previousStatus;
                                        })() : '—'}
                                    </div>
                                </div>

                                {/* 修改后状态 */}
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.currentStatus')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {(() => {
                                            const statusMap = {
                                                'MANUAL_APPROVED': t('enum.podReviewStatus.MANUAL_APPROVED'),
                                                'MANUAL_DISAPPROVED': t('enum.podReviewStatus.MANUAL_DISAPPROVED'),
                                                'MANUAL_CORRECTED': t('enum.podReviewStatus.MANUAL_CORRECTED')
                                            };

                                            // 直接使用 newStatus 字段，添加空值检查
                                            if (item.newStatus) {
                                                return statusMap[item.newStatus as keyof typeof statusMap] || item.newStatus;
                                            }
                                            return '—';
                                        })()}
                                    </div>
                                </div>

                                {/* 修改前理由 - 只有有理由时才显示 */}
                                {item.previousReviewNotes && item.previousReviewNotes.length > 0 && (
                                    <div>
                                        <div className="font-medium text-gray-700 mb-1">{t('components.timeline.previousReviewNotes')}:</div>
                                        <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                            {formatPodComment(item.previousReviewNotes)}
                                        </div>
                                    </div>
                                )}

                                {/* 修改后理由 */}
                                <div>
                                    <div className="font-medium text-gray-700 mb-1">{t('components.timeline.currentReviewNotes')}:</div>
                                    <div className="text-gray-600 whitespace-pre-line bg-white p-2 rounded border">
                                        {item.podComment && item.podComment.length > 0 ? formatPodComment(item.podComment) : '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {item.cameraPhotos && item.cameraPhotos.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {item.cameraPhotos.map((photo, idx) => (
                                <div key={idx} className="w-20 h-20 border border-gray-300 rounded overflow-hidden">
                                    <Image
                                        src={photo.s3Key}
                                        alt={`${t('components.timeline.exception')} ${idx + 1}`}
                                        width={80}
                                        height={80}
                                        className="object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex space-x-2">
                            {item.imageS3UrlsJson && item.imageS3UrlsJson.length > 0
                                && item.imageS3UrlsJson.map((photo, index) => (
                                    <div key={index} className="w-20 h-20 border border-gray-300 rounded overflow-hidden">
                                        <Image
                                            src={photo}
                                            alt={`${t('components.timeline.exception')} ${index + 1}`}
                                            width={80}
                                            height={80}
                                            className="object-cover"
                                        />
                                    </div>
                                ))}
                        </div>
                    )}
                    {externalCompare && item.eventId != null && (() => {
                        const list = externalCompare.byEventId.get(String(item.eventId));
                        if (!list?.length) return null;
                        return (
                            <div className="mt-3 space-y-3">
                                {list.map((ext, ei) => (
                                    <div
                                        key={`${String(item.eventId)}-ext-${ei}`}
                                        className="p-3 rounded-md bg-gray-100 border border-gray-200 text-sm"
                                    >
                                        <div className="flex justify-between gap-2">
                                            <span className="text-gray-900 flex-1 min-w-0 break-words">
                                                {getDisplayDescription(ext.description)}
                                            </span>
                                            <span className="text-gray-600 shrink-0 text-xs">
                                                {formatCompareEventTime(ext.eventTime, ext.localGmtOffset)}
                                            </span>
                                        </div>
                                        {getDisplayLocation(ext.location, t) && (
                                            <div className="text-gray-600 text-xs mt-1">
                                                {t('central.parcels.timeline.location', '站点')}: {getDisplayLocation(ext.location, t)}
                                            </div>
                                        )}
                                        {ext.operatorAccountName && ext.operatorAccountName.trim().length > 0 && (
                                            <div className="text-gray-600 text-xs mt-1">
                                                {t('components.timeline.operatorName')}: {ext.operatorAccountName.trim()}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            ),
        }));

        const unmappedTimeline =
            externalCompare && externalCompare.unmapped.length > 0
                ? [
                      {
                          key: 'unmapped-external' as const,
                          color: 'gray' as const,
                          children: (
                              <div className="space-y-3">
                                  <div className="text-sm font-medium text-gray-700">
                                      {t(
                                          'central.parcels.timeline.unmappedExternal',
                                          '未关联到内部事件的外部轨迹'
                                      )}
                                  </div>
                                  {externalCompare.unmapped.map((ext, ei) => (
                                      <div
                                          key={`unm-${ei}`}
                                          className="p-3 rounded-md bg-gray-100 border border-gray-200 text-sm"
                                      >
                                          <div className="flex justify-between gap-2">
                                              <span className="text-gray-900 flex-1 min-w-0 break-words">
                                                  {getDisplayDescription(ext.description)}
                                              </span>
                                              <span className="text-gray-600 shrink-0 text-xs">
                                                  {formatCompareEventTime(ext.eventTime, ext.localGmtOffset)}
                                              </span>
                                          </div>
                                          {getDisplayLocation(ext.location, t) && (
                                              <div className="text-gray-600 text-xs mt-1">
                                                  {t('central.parcels.timeline.location', '站点')}: {getDisplayLocation(ext.location, t)}
                                              </div>
                                          )}
                                          {ext.operatorAccountName && ext.operatorAccountName.trim().length > 0 && (
                                              <div className="text-gray-600 text-xs mt-1">
                                                  {t('components.timeline.operatorName')}: {ext.operatorAccountName.trim()}
                                              </div>
                                          )}
                                      </div>
                                  ))}
                              </div>
                          ),
                      },
                  ]
                : [];

        const timelineItems = [...internalNodes, ...unmappedTimeline];

        if (timelineItems.length < 8) return timelineItems;
        if (viewMore) return timelineItems;

        return [
            ...timelineItems.slice(0, maxItemsBeforeCollapse),
            {
                key: 'viewMore',
                color: 'gray',
                children: (
                    <div className="text-center cursor-pointer" onClick={() => setViewMore(true)}>
                        {t('components.timeline.viewMore')}
                    </div>
                )
            },
            timelineItems[timelineItems.length - 1]
        ];
    }, [items, viewMore, data, pickupExceptionData, channelName, getPickUpFailReasonText, maxItemsBeforeCollapse, formatPodComment, getNextActionTextFromBackend, currentLang, getExceptionReasonText, getExceptionSubReasonTexts, externalCompare]);

    return <Timeline items={displayedItems} />;
};

export default CustomParcelsTimeline;
